import type {ConfigType, Dayjs} from 'dayjs';

import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import {not} from '../conditionalUtils';
import {isDefined} from '../typecheck';
import {DATE_FORMAT_DEFAULT, DATE_FORMAT_L, DATE_FORMAT_SHORT} from './utils';

type FormatDateConfig = {
  appendYTT?: boolean;
  customFormat?: string;
  defaultFormat?: boolean;
  useUTC?: boolean;
  withYTT?: boolean;
  yttWithTime?: boolean;
};

export const formatDate = (
  date: ConfigType,
  {
    appendYTT = false,
    customFormat = '',
    defaultFormat = false,
    useUTC = false,
    withYTT = false, // YTT -  yesterday, today, tomorrow
    yttWithTime = false,
  }: FormatDateConfig = {},
): string => {
  const djsDate = dayjs(date, {utc: useUTC});

  if (defaultFormat) {
    return djsDate.format(DATE_FORMAT_DEFAULT);
  }

  const dateFormat =
    djsDate.year() === dayjs(undefined, {utc: useUTC}).year()
      ? DATE_FORMAT_SHORT
      : DATE_FORMAT_L;

  const dateStr = djsDate.format(customFormat || dateFormat);

  if (withYTT) {
    return makeDateWithYTT(djsDate, dateStr, {appendYTT, useUTC, yttWithTime});
  }

  return dateStr;
};

function makeDateWithYTT(
  djsDate: Dayjs,
  dateStr: string,
  {appendYTT, customFormat, useUTC, yttWithTime}: FormatDateConfig = {},
): string {
  if (isDefined(customFormat) && not(isDefined(appendYTT))) {
    // eslint-disable-next-line no-console
    console.warn(
      'appendYTT is not defined, but customFormat is defined. This will cause lost date information.',
    );
  }

  let timeStr = '';
  let _dateStr = '';

  if (appendYTT) {
    _dateStr = `, ${dateStr}`;
  }

  if (yttWithTime) {
    timeStr = ` ${djsDate.format('LT')}`;
  }

  const startOfDayDate = dayjs(undefined, {utc: useUTC}).startOf('day');
  const daysDiff = startOfDayDate.diff(djsDate.startOf('day'), 'day');

  switch (daysDiff) {
    case -1:
      return `${i18n.t('date-time:tomorrow')}${_dateStr}${timeStr}`;
    case 0:
      return `${i18n.t('date-time:today')}${_dateStr}${timeStr}`;
    case 1:
      return `${i18n.t('date-time:yesterday')}${_dateStr}${timeStr}`;
    default:
      return dateStr;
  }
}
